import React from "react";
import { Box, Card, CardContent, Stack, Typography, useTheme } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

const ACCENT_COLORS = {
  primary: { soft: "#EDE8FA", text: "#6C50BF", accent: "#6C50BF" },
  success: { soft: "#EDFAF2", text: "#1a7f27", accent: "#39c645" },
  warning: { soft: "#FFF4D6", text: "#8a6600", accent: "#f9bc06" },
  danger:  { soft: "#FAEDED", text: "#b30010", accent: "#ff1f35" },
  info:    { soft: "#EDF3FA", text: "#1e4ea8", accent: "#4178E5" },
  pink:    { soft: "#F4EDFA", text: "#8a3fb5", accent: "#ED7AA0" },
  cyan:    { soft: "#E6F6F4", text: "#2fa294", accent: "#53bdb1" },
};

export default function StatCard({
  label,
  value,
  icon,
  trend,
  helperText,
  color = "primary",
  onClick,
  gradient = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const scheme = ACCENT_COLORS[color] || ACCENT_COLORS.primary;

  if (gradient) {
    const gradientMap = {
      primary: "linear-gradient(135deg, #6C50BF 0%, #492ba3 100%)",
      success: "linear-gradient(135deg, #39c645 0%, #1a7f27 100%)",
      warning: "linear-gradient(135deg, #f9bc06 0%, #b88600 100%)",
      danger:  "linear-gradient(135deg, #ff1f35 0%, #b30010 100%)",
      info:    "linear-gradient(135deg, #4178E5 0%, #2758ba 100%)",
      pink:    "linear-gradient(135deg, #ED7AA0 0%, #d06287 100%)",
      cyan:    "linear-gradient(135deg, #53bdb1 0%, #2fa294 100%)",
    };
    return (
      <Card
        onClick={onClick}
        sx={{
          cursor: onClick ? "pointer" : "default",
          background: gradientMap[color] || gradientMap.primary,
          color: "white",
          border: "none",
          borderRadius: 4,
          overflow: "hidden",
          position: "relative",
          transition: "all 0.25s ease",
          "&:hover": onClick ? { transform: "translateY(-3px)", boxShadow: "0 12px 24px rgba(0,0,0,0.15)" } : {},
          "&::before": {
            content: '""',
            position: "absolute",
            top: -60,
            right: -60,
            width: 180,
            height: 180,
            background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -40,
            left: -40,
            width: 120,
            height: 120,
            background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
            borderRadius: "50%",
          },
        }}
      >
        <CardContent sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography
                variant="caption"
                sx={{ textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, opacity: 0.9 }}
              >
                {label}
              </Typography>
              <Typography sx={{ fontSize: "2rem", fontWeight: 700, mt: 0.5, lineHeight: 1.1 }}>
                {value}
              </Typography>
              {trend != null && (
                <Stack direction="row" alignItems="center" spacing={0.5} mt={0.75}>
                  {trend >= 0 ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11 }}>
                    {trend >= 0 ? "+" : ""}
                    {trend}% {helperText || ""}
                  </Typography>
                </Stack>
              )}
              {helperText && trend == null && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.85, fontSize: 11 }}>
                  {helperText}
                </Typography>
              )}
            </Box>
            {icon && (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {React.cloneElement(icon, { sx: { fontSize: 24, color: "white" } })}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        bgcolor: isDark ? "#121212" : "#F7F7F8",
        border: isDark ? "1px solid #262626" : "1px solid #eeeeee",
        borderRadius: { xs: 3, sm: 4 },
        borderBottom: `4px solid ${scheme.accent}`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": onClick ? { transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" } : {},
        // Native-feeling press feedback on touch.
        "&:active": onClick ? { transform: "scale(0.98)" } : {},
        height: "100%",
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 3 }, "&:last-child": { pb: { xs: 1.75, sm: 3 } } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 700,
                color: "text.secondary",
                fontSize: 10.5,
              }}
            >
              {label}
            </Typography>
            <Typography sx={{ fontSize: { xs: "1.4rem", sm: "1.75rem" }, fontWeight: 700, mt: 0.25, lineHeight: 1.15, color: "text.primary" }}>
              {value}
            </Typography>
            {trend != null && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.75}>
                {trend >= 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 14, color: "success.main" }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 14, color: "error.main" }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    color: trend >= 0 ? "success.main" : "error.main",
                  }}
                >
                  {trend >= 0 ? "+" : ""}
                  {trend}%
                </Typography>
                {helperText && (
                  <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>
                    {helperText}
                  </Typography>
                )}
              </Stack>
            )}
            {helperText && trend == null && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary", fontSize: 11 }}>
                {helperText}
              </Typography>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: { xs: 38, sm: 48 },
                height: { xs: 38, sm: 48 },
                borderRadius: 2,
                bgcolor: scheme.soft,
                color: scheme.text,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: { xs: 19, sm: 22 }, color: scheme.accent } })}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
